import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {pulseUserAtom} from 'atoms/auth';
import {growthbookCacheAtom} from 'atoms/growthbook';
import type {AiQueryTraceEntry} from 'components/ai/aiQueryConversation';
import {
  cancelSpeculativeRun,
  handOffSpeculativeRun,
  type SpeculativeProgress,
  type SpeculativeRun,
  startSpeculativeRun,
} from 'components/ai/speculativeAsk';
import {isSuperUser} from 'helpers/utils';
import {useAtomValue, useSetAtom} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * How long something has to stay in the panel before its Ask turn starts, so
 * sweeping the mouse across the list doesn't start a turn per row.
 */
const DWELL_MS = 700;

/**
 * Answers already seen in this tab, by question, so going back to something
 * shows its answer at once instead of starting another turn. The real box
 * would cache server-side per user (and use stored summaries for people).
 */
const CACHE_MS = 30 * 60 * 1000;

/** The one-line progress, plus everything streamed so far for the full answer. */
type LiveProgress = SpeculativeProgress & {
  answer: string;
  trace: AiQueryTraceEntry[];
  done: boolean;
};

// Only finished answers are kept, so a cached one is never cut off.
const answers = new Map<string, {progress: LiveProgress; at: number}>();

const cached = (question: string | null) => {
  const hit = question ? answers.get(question) : undefined;
  return hit && Date.now() - hit.at < CACHE_MS ? hit.progress : null;
};

/**
 * A real Ask turn for whatever the panel shows, started once it has been there
 * a moment (search's speculative Ask, so one turn per tab: a new one cancels
 * the last). `progress` is the latest step and first sentence, plus the whole
 * answer and its steps so far;
 * `open` hands the turn to the Ask drawer so it carries on rather than
 * starting over. An answer finished in the last half hour comes from the cache.
 */
export const useLiveAnswer = (question: string | null) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const {isSourcegraphEnabled} = useAtomValue(growthbookCacheAtom);
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const elevated = pulseUser ? isSuperUser(pulseUser) : false;
  const [progress, setProgress] = useState<LiveProgress | null>(null);
  const [started, setStarted] = useState(false);
  const runRef = useRef<SpeculativeRun | null>(null);

  useEffect(() => {
    const hit = cached(question);
    setProgress(hit);
    setStarted(Boolean(hit));
    if (!question || hit) return undefined;
    const timer = window.setTimeout(() => {
      const run = startSpeculativeRun(question, {
        elevated,
        enableSourcegraph: isSourcegraphEnabled,
      });
      runRef.current = run;
      setStarted(true);
      run.onProgress((next) => {
        const live = {...next, ...run.snapshot};
        setProgress(live);
        if (live.done && live.answer) answers.set(question, {progress: live, at: Date.now()});
      });
    }, DWELL_MS);
    return () => {
      window.clearTimeout(timer);
      const run = runRef.current;
      runRef.current = null;
      if (run) {
        run.onProgress(null);
        cancelSpeculativeRun(run);
      }
    };
  }, [question, elevated, isSourcegraphEnabled]);

  const open = useCallback(() => {
    if (!question) return;
    handOffSpeculativeRun(question);
    openAiSearchAndAsk(question, 'search_typeahead_row');
  }, [question, openAiSearchAndAsk]);

  return {progress, started, open};
};
